import { useMutation, useQueryClient } from '@tanstack/react-query';
import { post } from '@/shared/api/api-client';
import { invalidateIfNotInFlight } from '@/patients/lib/invalidate-if-not-in-flight';
import type { CreateImmunizationBody } from '@/types/patient-chart-api';

/**
 * POST /api/Patients/CreateImmunization
 *
 * Wire body curl-verified by Andrew 2026-05-20. See
 * `CreateImmunizationBody` type doc for the Immunization-specific
 * gotchas (most notably: PascalCase `Definition` preserved on every
 * Coding field, `administratedDate` typo preserved, `quantity` ships
 * as all-null wrapper not omitted, `statusReason: "n/a"` not null,
 * `patient` not `subject`).
 *
 * Cache: invalidates `['patient-immunizations', patientId]`. SignalR
 * `UpdatePatientChat` "Vaccinations" event from PROD-827 Slice 3 also
 * fires for concurrent sessions; the dedup helper short-circuits the
 * SignalR echo of THIS user's own save.
 */

interface CreateImmunizationParams {
  patientId: string;
  body: CreateImmunizationBody;
}

export function useCreateImmunization() {
  const qc = useQueryClient();
  return useMutation<string, Error, CreateImmunizationParams>({
    mutationFn: ({ body }) =>
      post<string>('/api/Patients/CreateImmunization', body),
    onSuccess: (_data, { patientId }) => {
      invalidateIfNotInFlight(qc, ['patient-immunizations', patientId]);
    },
  });
}
